import React, { useState, useEffect, useRef } from 'react';
import ProcurementDocuments from '../../Common/ProcurementDocuments';
import { toast } from 'react-hot-toast';
import inventoryApi from '../../../services/inventoryApi';
import SearchableInventorySupplierDropdown from '../../Common/SearchableInventorySupplierDropdown';
import SearchableLedgerDropdown from '../../Common/SearchableLedgerDropdown';
import SearchableCostCenterDropdown from '../../Common/SearchableCostCenterDropdown';
import LoadingSpinner from '../../Common/LoadingSpinner';

/**
 * Debit Note Form — create / edit a manual debit note against a supplier. Returned goods get their
 * debit note from the supplier return itself.
 *  - Price / quantity adjustment: from received (GRN) or completed (service) lines — price difference,
 *    overcharge, short or undone work. Limits per line: quantity ≤ still open (accepted − returned −
 *    other debit notes), amount per unit ≤ GRN unit price. Credits the account the GRN debited.
 *  - Penalty / liquidated damages, Rebate / discount: charge lines (description, reference, amount)
 *    credited to a ledger — penalty income / discount received by default.
 */
const NOTE_TYPES = [
  { key: 'ADJUSTMENT', label: 'Price / Quantity Adjustment', hint: 'Overcharge, price difference, short or undone work — against GRN / service completion lines. Reverses the cost and gives the budget back.' },
  { key: 'PENALTY', label: 'Penalty / Liquidated Damages', hint: 'Late or poor delivery under the contract — credited to penalty income. The cost and budget stay as they are; no VAT.' },
  { key: 'REBATE', label: 'Rebate / Discount', hint: 'Discount or rebate agreed after billing, can cover several bills — credited to discount received. Tax reverses VAT input.' },
];
const EMPTY_CHARGE = { description: '', reference: '', amount: '', ledgerAccountId: '', ledgerAccountLabel: '' };

const DebitNoteForm = ({ debitNote = null, onClose, onSuccess }) => {
  const isEdit = !!debitNote;
  const docsRef = useRef(null);
  const [noteType, setNoteType] = useState(debitNote?.noteType || 'ADJUSTMENT');
  const isCharge = noteType !== 'ADJUSTMENT';
  const [supplier, setSupplier] = useState(debitNote ? String(debitNote.supplier?._id || debitNote.supplier) : '');
  const [supplierLabel, setSupplierLabel] = useState(debitNote?.supplierName || debitNote?.supplier?.name || '');
  const [availableGRNs, setAvailableGRNs] = useState([]);
  const [loadingGRNs, setLoadingGRNs] = useState(false);
  const [selected, setSelected] = useState({}); // key -> { grn, item, quantity, unitPrice }
  const [charges, setCharges] = useState(() => (debitNote?.chargeLines || []).map(c => ({
    description: c.description || '', reference: c.reference || '', amount: c.amount ?? '',
    ledgerAccountId: String(c.ledgerAccount?.accountId || ''), ledgerAccountLabel: c.ledgerAccount?.label || ''
  })));
  const [chargeDefaults, setChargeDefaults] = useState({});
  const [costCenter, setCostCenter] = useState(debitNote?.costCenter ? String(debitNote.costCenter) : '');
  const [costCenterLabel, setCostCenterLabel] = useState(debitNote?.costCenterLabel || '');
  // penalty / rebate: the GRN it relates to (optional) — fills the reference and, when none is chosen, the cost center
  const [relatedGrn, setRelatedGrn] = useState(debitNote?.relatedGrn ? String(debitNote.relatedGrn?._id || debitNote.relatedGrn) : '');
  const [costCenterFromGrn, setCostCenterFromGrn] = useState(false);
  const [taxAmount, setTaxAmount] = useState(debitNote?.totals?.taxAmount || 0);
  const [reasonDescription, setReasonDescription] = useState(debitNote?.reasonDescription || '');
  const [notes, setNotes] = useState(debitNote?.notes || '');
  const [debitDate, setDebitDate] = useState((debitNote?.debitDate ? new Date(debitNote.debitDate) : new Date()).toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  // Default credit accounts for penalty / rebate lines (Finance → Transaction Ledger Mapping)
  useEffect(() => {
    inventoryApi.getDebitNoteChargeDefaults()
      .then(res => setChargeDefaults(res.data || {}))
      .catch(() => setChargeDefaults({}));
  }, []);

  useEffect(() => {
    if (supplier) loadGRNs(supplier);
    else if (!supplier) { setAvailableGRNs([]); setSelected({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier, isCharge]);

  // A charge note starts with one line on the type's default account
  const blankCharge = (type = noteType) => ({
    ...EMPTY_CHARGE,
    ledgerAccountId: chargeDefaults[type]?.accountId || '',
    ledgerAccountLabel: chargeDefaults[type]?.label || ''
  });
  useEffect(() => {
    if (isCharge && charges.length === 0) setCharges([blankCharge()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCharge]);
  // Defaults arrive after the first line was added: fill lines that have no account yet
  useEffect(() => {
    const d = chargeDefaults[noteType];
    if (!d) return;
    setCharges(prev => prev.map(c => (c.ledgerAccountId ? c : { ...c, ledgerAccountId: d.accountId, ledgerAccountLabel: d.label })));
  }, [chargeDefaults, noteType]);

  const changeType = (type) => {
    if (type === noteType) return;
    const d = chargeDefaults[noteType];
    setNoteType(type);
    if (type === 'PENALTY') setTaxAmount(0);
    // lines still on the previous type's default account move to the new type's default
    setCharges(prev => prev.map(c => (c.ledgerAccountId && c.ledgerAccountId !== d?.accountId ? c : {
      ...c, ledgerAccountId: chargeDefaults[type]?.accountId || '', ledgerAccountLabel: chargeDefaults[type]?.label || ''
    })));
  };

  const itemKey = (grnId, itemId) => `${grnId}::${itemId}`;
  const refOf = (x) => String(x?.assetId || x?.serviceId?._id || x?.serviceId || x?.item?._id || x?.item || '');

  const loadGRNs = async (supplierId) => {
    setLoadingGRNs(true);
    try {
      const res = await inventoryApi.getAvailableGRNsForDebitNote(supplierId, debitNote?._id);
      const grns = res.data || [];
      setAvailableGRNs(grns);
      // Editing: pre-select the note's lines
      const pre = {};
      if (debitNote && String(debitNote.supplier?._id || debitNote.supplier) === String(supplierId)) {
        for (const li of debitNote.lineItems || []) {
          const grn = grns.find(g => String(g._id) === String(li.sourceGrn?._id || li.sourceGrn));
          const item = grn?.items.find(i => refOf(i) === refOf(li));
          if (grn && item) pre[itemKey(grn._id, item._id)] = { grn, item, quantity: li.quantity, unitPrice: li.unitPrice };
        }
      }
      setSelected(pre);
    } catch (err) {
      console.error('Error loading GRNs:', err);
      toast.error('Failed to load GRNs for this supplier');
      setAvailableGRNs([]);
    } finally {
      setLoadingGRNs(false);
    }
  };

  const toggleItem = (grn, item) => {
    const key = itemKey(grn._id, item._id);
    setSelected(prev => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = { grn, item, quantity: item.quantity || 1, unitPrice: item.unitPrice || 0 };
      }
      return next;
    });
  };

  const updateSelected = (key, field, value) => {
    setSelected(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: parseFloat(value) || 0 }
    }));
  };

  // A related GRN fills an empty reference and, when no cost center was chosen, its PO's cost center
  const pickRelatedGrn = (id) => {
    const prevGrn = availableGRNs.find(g => String(g._id) === relatedGrn);
    setRelatedGrn(id);
    const g = availableGRNs.find(x => String(x._id) === id);
    const ref = g ? [g.grnNumber, g.purchaseOrderNumber].filter(Boolean).join(' / ') : '';
    const prevRef = prevGrn ? [prevGrn.grnNumber, prevGrn.purchaseOrderNumber].filter(Boolean).join(' / ') : '';
    setCharges(prev => prev.map((c, i) => (i === 0 && (!c.reference || c.reference === prevRef) ? { ...c, reference: ref } : c)));
    if (!costCenter || costCenterFromGrn) {
      setCostCenter(g?.costCenter || '');
      setCostCenterLabel(g?.costCenterLabel || '');
      setCostCenterFromGrn(!!g?.costCenter);
    }
  };

  const updateCharge = (i, patch) => setCharges(prev => prev.map((c, j) => (j === i ? { ...c, ...patch } : c)));

  const selectedList = Object.entries(selected).map(([key, v]) => ({ key, ...v }));
  const subtotal = isCharge
    ? charges.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0)
    : selectedList.reduce((sum, s) => sum + (s.quantity * s.unitPrice), 0);
  const grandTotal = subtotal + (parseFloat(taxAmount) || 0);

  // Line limits (the server checks the same)
  const lineError = (s) => {
    if (!(s.quantity > 0)) return 'Quantity must be more than 0';
    if (s.quantity > (s.item.quantity || 0) + 0.0001) return `Only ${s.item.quantity} open`;
    if (!(s.unitPrice > 0)) return 'Amount per unit must be more than 0';
    if (s.unitPrice > (s.item.unitPrice || 0) + 0.0001) return `Max ${Number(s.item.unitPrice || 0).toFixed(2)} (GRN price)`;
    return null;
  };
  const chargeError = (c) => {
    if (!String(c.description).trim()) return 'Description is required';
    if (!((parseFloat(c.amount) || 0) > 0)) return 'Amount must be more than 0';
    if (!c.ledgerAccountId) return 'Choose the account to credit';
    return null;
  };

  const handleSubmit = async () => {
    if (!supplier) { toast.error('Please select a supplier'); return; }
    if ((parseFloat(taxAmount) || 0) < 0) { toast.error('Tax amount cannot be negative'); return; }

    const payload = {
      supplier,
      noteType,
      debitDate,
      reasonDescription,
      taxAmount: noteType === 'PENALTY' ? 0 : (parseFloat(taxAmount) || 0),
      notes
    };

    if (isCharge) {
      if (charges.length === 0) { toast.error('Add at least one charge line'); return; }
      const badIndex = charges.findIndex(c => chargeError(c));
      if (badIndex >= 0) { toast.error(`Line ${badIndex + 1}: ${chargeError(charges[badIndex])}`); return; }
      payload.chargeLines = charges.map(c => ({
        description: c.description.trim(),
        reference: c.reference.trim(),
        amount: parseFloat(c.amount) || 0,
        ledgerAccount: { accountId: c.ledgerAccountId, label: c.ledgerAccountLabel }
      }));
      payload.costCenter = costCenter || null;
      payload.relatedGrn = relatedGrn || null;
      payload.costCenterLabel = costCenterLabel;
    } else {
      if (selectedList.length === 0) { toast.error('Select at least one line item'); return; }
      const bad = selectedList.find(s => lineError(s));
      if (bad) { toast.error(`${bad.item.itemName}: ${lineError(bad)}`); return; }
      payload.lineItems = selectedList.map(s => ({
        ...(s.item.assetId ? { assetId: s.item.assetId }
          : s.item.serviceId ? { serviceId: s.item.serviceId }
          : { item: s.item.item?._id || s.item.item }),
        itemCode: s.item.itemCode,
        itemName: s.item.itemName,
        sourceGrn: s.grn._id,
        sourceGrnNumber: s.grn.grnNumber,
        quantity: s.quantity,
        unitPrice: s.unitPrice,
        unit: s.item.unit
      }));
    }

    setSaving(true);
    try {
      if (isEdit) {
        await inventoryApi.updateDebitNote(debitNote._id, payload);
        toast.success('Debit note updated successfully');
      } else {
        const res = await inventoryApi.createDebitNote(payload);
        toast.success('Debit note created successfully');
        await docsRef.current?.uploadPending(res?.data?._id);
      }
      onSuccess?.();
    } catch (err) {
      console.error('Error saving debit note:', err);
      toast.error(err.response?.data?.message || 'Failed to save debit note');
    } finally {
      setSaving(false);
    }
  };

  const typeInfo = NOTE_TYPES.find(t => t.key === noteType);

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-5xl max-h-[92vh] bg-white shadow-2xl rounded-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">{isEdit ? `Edit Debit Note ${debitNote.debitNoteNumber}` : 'New Debit Note'}</h3>
            <p className="text-xs text-amber-100">Adjustment, penalty or rebate — for returned goods use From Supplier Returns</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Debit Note Type <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {NOTE_TYPES.map(t => (
                <label key={t.key}
                  className={`flex items-start gap-2 p-2.5 rounded-lg border-2 text-sm ${noteType === t.key ? 'border-amber-500 bg-amber-50' : 'border-gray-200'} ${isEdit ? 'cursor-not-allowed opacity-80' : 'cursor-pointer hover:border-amber-300'}`}>
                  <input type="radio" name="noteType" className="mt-0.5" checked={noteType === t.key} disabled={isEdit}
                    onChange={() => changeType(t.key)} />
                  <span className="font-medium text-gray-800">{t.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">{typeInfo?.hint}</p>
          </div>

          {/* Supplier + Date (+ cost center for charges) */}
          <div className={`grid grid-cols-1 gap-4 ${isCharge ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Supplier <span className="text-red-500">*</span></label>
              {isEdit ? (
                <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-800">{supplierLabel || '—'}</div>
              ) : (
                <SearchableInventorySupplierDropdown
                  value={supplier}
                  displayLabel={supplierLabel}
                  onChange={(val, label) => { setSupplier(val); setSupplierLabel(label || ''); }}
                  placeholder="Select supplier"
                  menuPlacement="down"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Debit Date</label>
              <input
                type="date"
                value={debitDate}
                onChange={(e) => setDebitDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            {isCharge && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Cost Center</label>
                <SearchableCostCenterDropdown
                  value={costCenter}
                  displayLabel={costCenterLabel || (costCenter && costCenterFromGrn ? 'From the related GRN' : '')}
                  onChange={(id, label) => { setCostCenter(id || ''); setCostCenterLabel(label || ''); setCostCenterFromGrn(false); }}
                  placeholder="Optional"
                  menuPlacement="down"
                />
                {costCenterFromGrn && costCenter && <p className="text-[11px] text-gray-500 mt-0.5">Taken from the related GRN's PO</p>}
              </div>
            )}
            {isCharge && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Related GRN</label>
                <select
                  value={relatedGrn}
                  disabled={!supplier}
                  onChange={(e) => pickRelatedGrn(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                  data-testid="related-grn"
                >
                  <option value="">{supplier ? 'Optional — none' : 'Select a supplier first'}</option>
                  {relatedGrn && !availableGRNs.some(g => String(g._id) === relatedGrn) && (
                    <option value={relatedGrn}>{debitNote?.relatedGrnNumber || 'Current GRN'}</option>
                  )}
                  {availableGRNs.map(g => (
                    <option key={g._id} value={g._id}>{g.grnNumber}{g.purchaseOrderNumber ? ` · ${g.purchaseOrderNumber}` : ''}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {isCharge ? (
            /* Charge lines */
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 flex flex-wrap justify-between gap-2">
                <span>{noteType === 'PENALTY' ? 'Penalty Lines' : 'Rebate / Discount Lines'}</span>
                <span className="text-xs font-normal text-gray-500">
                  {chargeDefaults[noteType]
                    ? `Default account: ${chargeDefaults[noteType].label}`
                    : `No default account — set "${noteType === 'PENALTY' ? 'Debit Note — Supplier Penalty' : 'Debit Note — Rebate / Discount'}" in Transaction Ledger Mapping, or choose one per line`}
                </span>
              </div>
              <div className="p-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500">
                    <tr>
                      <th className="px-2 py-1 text-left w-8">#</th>
                      <th className="px-2 py-1 text-left">Description *</th>
                      <th className="px-2 py-1 text-left w-40">Reference</th>
                      <th className="px-2 py-1 text-left w-64">Credit Account *</th>
                      <th className="px-2 py-1 text-right w-32">Amount *</th>
                      <th className="px-2 py-1 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {charges.map((c, i) => (
                      <tr key={i} className="border-t border-gray-100 align-top">
                        <td className="px-2 py-1.5 text-gray-500">{i + 1}</td>
                        <td className="px-2 py-1.5">
                          <input value={c.description} onChange={(e) => updateCharge(i, { description: e.target.value })}
                            placeholder={noteType === 'PENALTY' ? 'e.g. Late completion — 5 days × 2,000' : 'e.g. Volume rebate Q3'}
                            className="w-full px-2 py-1 border border-gray-300 rounded" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input value={c.reference} onChange={(e) => updateCharge(i, { reference: e.target.value })}
                            placeholder="Invoice / PO / contract"
                            className="w-full px-2 py-1 border border-gray-300 rounded" />
                        </td>
                        <td className="px-2 py-1.5">
                          <SearchableLedgerDropdown
                            value={c.ledgerAccountId}
                            displayLabel={c.ledgerAccountLabel}
                            onChange={(id, lbl) => updateCharge(i, { ledgerAccountId: id || '', ledgerAccountLabel: lbl || '' })}
                            ledgerGroup={['REVENUE', 'EXPENSES']}
                            placeholder="Select account…"
                            menuPlacement="auto"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <input type="number" min="0.01" step="0.01" value={c.amount} onChange={(e) => updateCharge(i, { amount: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-right" />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {charges.length > 1 && (
                            <button onClick={() => setCharges(prev => prev.filter((_, j) => j !== i))} title="Remove line"
                              className="text-gray-400 hover:text-red-600">✕</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button onClick={() => setCharges(prev => [...prev, blankCharge()])}
                  className="mt-2 px-3 py-1 text-xs font-medium text-amber-700 border border-amber-300 rounded hover:bg-amber-50">
                  + Add line
                </button>
              </div>
            </div>
          ) : (
            /* GRN / service completion line items */
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 flex flex-wrap justify-between gap-2">
                <span>Select GRN / Service Completion Lines</span>
                <span className="text-xs font-normal text-gray-500">Open = accepted − returned − on other debit notes · Amount / unit up to the GRN price</span>
              </div>
              <div className="p-3">
                {!supplier ? (
                  <p className="text-sm text-gray-500 py-4 text-center">Select a supplier to load received items and completed services</p>
                ) : loadingGRNs ? (
                  <div className="flex items-center justify-center py-6"><LoadingSpinner /><span className="ml-2 text-gray-600">Loading GRNs...</span></div>
                ) : availableGRNs.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">No GRN or service completion lines left open for a debit note for this supplier</p>
                ) : (
                  <div className="space-y-4">
                    {availableGRNs.map(grn => {
                      const isService = grn.procurementType === 'SERVICE';
                      return (
                        <div key={grn._id} className="border border-gray-200 rounded overflow-x-auto">
                          <div className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 flex justify-between">
                            <span>{grn.grnNumber} · {grn.receiptDate ? new Date(grn.receiptDate).toLocaleDateString() : ''}{grn.purchaseOrderNumber ? ` · ${grn.purchaseOrderNumber}` : ''}</span>
                            <span>{isService ? 'SERVICE COMPLETION' : grn.procurementType}</span>
                          </div>
                          <table className="w-full text-sm">
                            <thead className="bg-white text-xs text-gray-500">
                              <tr>
                                <th className="px-2 py-1 text-left w-8"></th>
                                <th className="px-2 py-1 text-left">{isService ? 'Service' : 'Item'}</th>
                                <th className="px-2 py-1 text-right">{isService ? 'Completed' : 'Accepted'}</th>
                                <th className="px-2 py-1 text-right">Returned</th>
                                <th className="px-2 py-1 text-right">Other DN</th>
                                <th className="px-2 py-1 text-right">Open</th>
                                <th className="px-2 py-1 text-right">GRN Price</th>
                                <th className="px-2 py-1 text-right">Qty</th>
                                <th className="px-2 py-1 text-right">Amount / Unit</th>
                                <th className="px-2 py-1 text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(grn.items || []).map(item => {
                                const key = itemKey(grn._id, item._id);
                                const sel = selected[key];
                                const err = sel ? lineError({ ...sel, item }) : null;
                                return (
                                  <tr key={key} className={`border-t border-gray-100 ${err ? 'bg-red-50' : ''}`}>
                                    <td className="px-2 py-1">
                                      <input type="checkbox" checked={!!sel} onChange={() => toggleItem(grn, item)} />
                                    </td>
                                    <td className="px-2 py-1">
                                      <div className="font-medium text-gray-800">{item.itemName}</div>
                                      <div className="text-xs text-gray-500">{item.itemCode}{item.unit ? ` · ${item.unit}` : ''}</div>
                                      {err && <div className="text-xs text-red-600">{err}</div>}
                                    </td>
                                    <td className="px-2 py-1 text-right text-gray-600">{item.acceptedQuantity}</td>
                                    <td className="px-2 py-1 text-right text-gray-500">{isService ? '—' : (item.returnedQuantity || '-')}</td>
                                    <td className="px-2 py-1 text-right text-gray-500">{item.debitedQuantity || '-'}</td>
                                    <td className="px-2 py-1 text-right font-semibold text-gray-800">{item.quantity}</td>
                                    <td className="px-2 py-1 text-right text-gray-600">{Number(item.unitPrice || 0).toFixed(2)}</td>
                                    <td className="px-2 py-1 text-right">
                                      {sel ? (
                                        <input type="number" min="0.01" step="0.01" max={item.quantity} value={sel.quantity}
                                          onChange={(e) => updateSelected(key, 'quantity', e.target.value)}
                                          className="w-20 px-1 py-0.5 border border-gray-300 rounded text-right" />
                                      ) : '-'}
                                    </td>
                                    <td className="px-2 py-1 text-right">
                                      {sel ? (
                                        <input type="number" min="0.01" step="0.01" max={item.unitPrice} value={sel.unitPrice}
                                          onChange={(e) => updateSelected(key, 'unitPrice', e.target.value)}
                                          className="w-24 px-1 py-0.5 border border-gray-300 rounded text-right" />
                                      ) : '-'}
                                    </td>
                                    <td className="px-2 py-1 text-right font-medium">
                                      {sel ? (sel.quantity * sel.unitPrice).toFixed(2) : '-'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reason / notes / totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Reason / Description</label>
              <textarea rows="3" value={reasonDescription} onChange={(e) => setReasonDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none text-sm"
                placeholder={noteType === 'PENALTY' ? 'Contract clause / SLA breached...' : noteType === 'REBATE' ? 'Rebate / discount agreement...' : 'Reason for the debit note (e.g. price difference, overcharge, work not done)...'} />
              <label className="block text-sm font-semibold text-gray-700 mb-1 mt-3">Notes</label>
              <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none text-sm" />
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 h-fit">
              <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal</span><span className="font-medium">{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-gray-600">Tax Amount <span className="text-xs text-gray-400">{noteType === 'PENALTY' ? '(none on a penalty)' : noteType === 'REBATE' ? '(reverses VAT input)' : '(reverses SSCL / VAT)'}</span></span>
                <input type="number" min="0" step="0.01" value={noteType === 'PENALTY' ? 0 : taxAmount} disabled={noteType === 'PENALTY'}
                  onChange={(e) => setTaxAmount(e.target.value)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-right disabled:bg-gray-100 disabled:text-gray-400" />
              </div>
              <div className="flex justify-between text-base font-bold border-t border-gray-300 pt-2">
                <span>Total</span><span className="text-amber-700">{grandTotal.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-500">On approval: Dr supplier · Cr {noteType === 'ADJUSTMENT' ? 'the stock / asset / service expense account the GRN debited' : 'the accounts above'}. Set off in a later supplier payment.</p>
            </div>
          </div>

          {/* Supporting documents: supplier credit note, contract clause, correspondence… */}
          <ProcurementDocuments ref={docsRef} docType="debit-notes" docId={debitNote?._id || null} />
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-gray-50 px-5 py-3 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-1.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white text-sm font-medium rounded-lg disabled:opacity-50">
            {saving ? 'Saving...' : isEdit ? 'Update Debit Note' : 'Create Debit Note'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebitNoteForm;
